/**
 * Table des sections de la console : la SEULE description de ce que montre le
 * back-office. Le menu (8 entrées), les onglets et les routes en sont déduits
 * (`components/navigation`, `routes/routesDesSections`) : ajouter un écran =
 * ajouter un onglet ici, à sa place.
 *
 * Les écrans sont chargés à l'ouverture de leur onglet (`lazy`) : la console
 * ne télécharge que ce qu'on regarde, et cette table se lit sans les écrans.
 *
 * Rôles : absent = tout compte back-office (comme les routes historiques) ;
 * le niveau le plus proche de l'écran l'emporte. Le backend reste juge.
 */
import React, { lazy } from 'react';
import {
  Build as TechniqueIcon,
  Dashboard as TableauDeBordIcon,
  Folder as ContenuIcon,
  Payments as PaiementsIcon,
  People as UtilisateursIcon,
  Campaign as CroissanceIcon,
  Settings as ReglagesIcon,
  Shield as ModerationIcon,
} from '@mui/icons-material';
import { UserRole } from '../types/rbac';
import { ADMINS, SUPER_ADMINS } from './roleSets';
const Dashboard = lazy(() => import('../components/Dashboard'));
const ClasseList = lazy(() => import('../components/classes/ClasseList'));
const MatiereList = lazy(() => import('../components/matieres/MatiereList'));
const VideoList = lazy(() => import('../components/videos/VideoList'));
const SchoolBookList = lazy(() => import('../components/school-books/SchoolBookList'));
const BookExerciseList = lazy(() => import('../components/school-books/BookExerciseList'));
const ThumbnailsPage = lazy(() => import('../pages/ThumbnailsPage'));
const FeaturedVideosPage = lazy(() => import('../pages/FeaturedVideosPage'));
const UserStatsPage = lazy(() => import('../pages/UserStatsPage'));
const CreditsSuiviPage = lazy(() => import('../pages/CreditsSuiviPage'));
const RevenusPage = lazy(() => import('../pages/RevenusPage'));
const PubliciteActivitePage = lazy(() => import('../pages/PubliciteActivitePage'));
const AgentStatsPage = lazy(() => import('../pages/AgentStatsPage'));
const MobileUsersPage = lazy(() => import('../pages/MobileUsersPage'));
const FicheUtilisateur = lazy(() => import('../features/fiches/pages/FicheUtilisateur'));
const FicheEnseignant = lazy(() => import('../features/fiches/pages/FicheEnseignant'));
const ListeEnseignants = lazy(() => import('../features/fiches/pages/ListeEnseignants'));
const UserManagementPage = lazy(() => import('../pages/UserManagementPage'));
const InvitationCodesPage = lazy(() => import('../pages/InvitationCodesPage'));
const PackagesList = lazy(() => import('../components/payments/PackagesList'));
const CreditsList = lazy(() => import('../components/payments/CreditsList'));
const CampaignsPage = lazy(() => import('../pages/CampaignsPage'));
const PricingPage = lazy(() => import('../pages/PricingPage'));
const ReglagesCoutsPage = lazy(() => import('../pages/ReglagesCoutsPage'));
const PubliciteConfigPage = lazy(() => import('../pages/PubliciteConfigPage'));
const AgentConfigPage = lazy(() => import('../pages/AgentConfigPage'));
const AgentProfilesPage = lazy(() => import('../pages/AgentProfilesPage'));
const AgentSecretsPage = lazy(() => import('../pages/AgentSecretsPage'));
const AgentPlaygroundPage = lazy(() => import('../pages/AgentPlaygroundPage'));
const ReglagesEnseignantsPage = lazy(() => import('../pages/ReglagesEnseignantsPage'));
const ReglagesModerationPage = lazy(() => import('../pages/ReglagesModerationPage'));
const ReglagesVideosCard = lazy(() => import('../components/videos/ReglagesVideosCard'));
const ReglagesAlertesCard = lazy(() => import('../components/crash/ReglagesAlertesCard'));
const ActivityMonitorPage = lazy(() => import('../pages/ActivityMonitorPage'));
const ActivityLogDetailPage = lazy(() => import('../pages/ActivityLogDetailPage'));
const CrashReportsPage = lazy(() => import('../pages/CrashReportsPage'));
const EnvSyncPage = lazy(() => import('../pages/env-sync/EnvSyncPage'));
const EnvSyncHistoryPage = lazy(() => import('../pages/env-sync/EnvSyncHistoryPage'));

/**
 * Écran exporté par nom depuis l'index d'une fonctionnalité, chargé à
 * l'ouverture de son onglet comme les autres.
 */
const ecranNomme = <Module,>(charger: () => Promise<Module>, nom: keyof Module) =>
  lazy(() => charger().then((module) => ({ default: module[nom] as unknown as React.ComponentType })));
const chargerLogs = () => import('../pages/logs');
const LogsPage = ecranNomme(chargerLogs, 'LogsPage');
const chargerDataMigration = () => import('../features/data-migration');
const DataMigrationPage = ecranNomme(chargerDataMigration, 'DataMigrationPage');
const chargerPispi = () => import('../features/pispi');
const PispiClientPage = ecranNomme(chargerPispi, 'PispiClientPage');
const PispiClientsPage = ecranNomme(chargerPispi, 'PispiClientsPage');
const PispiComptePage = ecranNomme(chargerPispi, 'PispiComptePage');
const PispiConfigurationPage = ecranNomme(chargerPispi, 'PispiConfigurationPage');
const PispiNotificationsPage = ecranNomme(chargerPispi, 'PispiNotificationsPage');
const PispiPaiementsPage = ecranNomme(chargerPispi, 'PispiPaiementsPage');
const PispiSimulateurPage = ecranNomme(chargerPispi, 'PispiSimulateurPage');
const chargerModeration = () => import('../features/moderation');
const AFaire = ecranNomme(chargerModeration, 'AFaire');
const Suivi = ecranNomme(chargerModeration, 'Suivi');
const chargerContenu = () => import('../features/contenu');
const ExplorateurProgramme = ecranNomme(chargerContenu, 'ExplorateurProgramme');
const ExplorateurManuels = ecranNomme(chargerContenu, 'ExplorateurManuels');
const ProgrammeOfficiel = ecranNomme(chargerContenu, 'ProgrammeOfficiel');
const StrategieExamen = ecranNomme(chargerContenu, 'StrategieExamen');
const chargerParrainage = () => import('../features/parrainage');
const ParrainagePage = ecranNomme(chargerParrainage, 'ParrainagePage');
const ReglagesParrainagePage = ecranNomme(chargerParrainage, 'ReglagesParrainagePage');

/** Fiche de détail ouverte depuis un onglet (l'onglet reste sélectionné). */
export interface Detail {
  /** Relatif à l'onglet, avec ses paramètres (`:uid`). */
  chemin: string;
  element: React.ReactElement;
}

/** Onglet d'une section : un écran, ou des sous-onglets (un niveau de plus). */
export interface Onglet {
  /** Unique parmi ses frères. */
  cle: string;
  titre: string;
  /** Relatif au parent ; '' pour l'onglet à la racine du parent. */
  chemin: string;
  /** Écran de l'onglet ; absent quand il a des sous-onglets. */
  element?: React.ReactElement;
  /** Absent : rôles du parent. */
  roles?: UserRole[];
  onglets?: Onglet[];
  details?: Detail[];
}

/** Entrée du menu et page à onglets. */
export interface Section {
  cle: string;
  titre: string;
  icone: React.ReactElement;
  /** Adresse absolue de la section. */
  chemin: string;
  /** Absent : tout compte back-office. */
  roles?: UserRole[];
  onglets: Onglet[];
}

/** Onglet final, sans sous-onglets. */
const ecran = (cle: string, titre: string, element: React.ReactElement, roles?: UserRole[]): Onglet => ({
  cle, titre, chemin: cle, element, roles,
});

const TABLEAU_DE_BORD: Section = {
  cle: 'tableau-de-bord', titre: 'Tableau de bord', icone: <TableauDeBordIcon />, chemin: '/tableau-de-bord',
  onglets: [
    { cle: 'vue', titre: "Vue d'ensemble", chemin: '', element: <Dashboard /> },
    ecran('utilisateurs', 'Utilisateurs', <UserStatsPage />, ADMINS),
    ecran('credits', 'Crédits', <CreditsSuiviPage />, ADMINS),
    ecran('revenus', 'Revenus', <RevenusPage />, ADMINS),
    ecran('publicite', 'Publicité', <PubliciteActivitePage />, ADMINS),
    ecran('suklu-ai', 'Suklu AI', <AgentStatsPage />, ADMINS),
  ],
};

const CONTENU: Section = {
  cle: 'contenu', titre: 'Contenu', icone: <ContenuIcon />, chemin: '/contenu',
  onglets: [
    ecran('programme', 'Programme', <ExplorateurProgramme />),
    // Carte vmJaRl8k : progression officielle par classe × matière (lecture ouverte au lecteur).
    ecran('programme-officiel', 'Programme officiel', <ProgrammeOfficiel />),
    // Cartes orB8XTzT, Ku1BWOr0 : fiches et guides BEPC par matière (admins : l'API refuse le lecteur).
    ecran('strategie-examen', "Stratégie d'examen", <StrategieExamen />, ADMINS),
    ecran('manuels', 'Manuels scolaires', <ExplorateurManuels />),
    ecran('a-la-une', 'Vidéos à la une', <FeaturedVideosPage />, ADMINS),
    {
      // Lot 2 : ce que l'explorateur ne couvre pas (création des classes, matières,
      // vidéos et livres ; actions en lot ; vignettes des autres collections).
      cle: 'referentiel', titre: 'Référentiel', chemin: 'referentiel',
      onglets: [
        ecran('classes', 'Classes', <ClasseList />),
        ecran('matieres', 'Matières', <MatiereList />),
        ecran('videos', 'Vidéothèque', <VideoList />),
        ecran('livres', 'Livres', <SchoolBookList />),
        ecran('exercices-manuels', 'Exercices de manuels', <BookExerciseList />),
        ecran('vignettes', 'Vignettes', <ThumbnailsPage />, ADMINS),
      ],
    },
  ],
};

const UTILISATEURS: Section = {
  cle: 'utilisateurs', titre: 'Utilisateurs', icone: <UtilisateursIcon />, chemin: '/utilisateurs', roles: ADMINS,
  onglets: [
    {
      ...ecran('eleves-parents', 'Élèves & parents', <MobileUsersPage />),
      details: [{ chemin: ':uid', element: <FicheUtilisateur /> }],
    },
    {
      // Revue du 21/10 : une fiche unique par enseignant (pièces, analyse IA, décision).
      ...ecran('enseignants', 'Enseignants', <ListeEnseignants />),
      details: [{ chemin: ':uid', element: <FicheEnseignant /> }],
    },
    {
      // Revue du 21/10 : inviter quelqu'un fait partie de la gestion de l'équipe.
      cle: 'equipe', titre: 'Équipe back-office', chemin: 'equipe',
      onglets: [
        ecran('comptes', 'Comptes', <UserManagementPage />),
        ecran('invitations', "Codes d'invitation", <InvitationCodesPage />),
      ],
    },
  ],
};

const MODERATION: Section = {
  cle: 'moderation', titre: 'Modération', icone: <ModerationIcon />, chemin: '/moderation', roles: ADMINS,
  onglets: [
    // Revue du 21/10 : une seule file pour messagerie, détresse, incidents, IA et exercices.
    ecran('a-traiter', 'À traiter', <AFaire />),
    ecran('suivi', 'Suivi', <Suivi />),
  ],
};

const PAIEMENTS: Section = {
  cle: 'paiements', titre: 'Paiements', icone: <PaiementsIcon />, chemin: '/paiements', roles: ADMINS,
  onglets: [
    ecran('recus', 'Paiements reçus', <PispiPaiementsPage />, SUPER_ADMINS),
    {
      ...ecran('clients', 'Clients', <PispiClientsPage />, SUPER_ADMINS),
      details: [{ chemin: ':uid', element: <PispiClientPage /> }],
    },
    ecran('packs', 'Packs', <PackagesList />, SUPER_ADMINS),
    ecran('credits', 'Ajuster des crédits', <CreditsList />, SUPER_ADMINS),
    ecran('parrainage', 'Parrainage', <ParrainagePage />),
  ],
};

const CROISSANCE: Section = {
  cle: 'croissance', titre: 'Croissance', icone: <CroissanceIcon />, chemin: '/croissance', roles: ADMINS,
  onglets: [ecran('campagnes', 'Campagnes', <CampaignsPage />)],
};

const REGLAGES: Section = {
  cle: 'reglages', titre: 'Réglages', icone: <ReglagesIcon />, chemin: '/reglages', roles: SUPER_ADMINS,
  onglets: [
    ecran('tarifs', 'Tarifs & catalogue', <PricingPage />),
    ecran('couts-ia', 'Coûts & crédits IA', <ReglagesCoutsPage />),
    ecran('publicite', 'Publicité', <PubliciteConfigPage />),
    {
      cle: 'suklu-ai', titre: 'Suklu AI', chemin: 'suklu-ai',
      onglets: [
        ecran('modeles', 'Modèles & réponses', <AgentConfigPage />),
        ecran('profils', 'Profils par audience', <AgentProfilesPage />),
        ecran('cles', 'Clés & intégrations', <AgentSecretsPage />),
        ecran('bac-a-essai', 'Bac à essai', <AgentPlaygroundPage />),
      ],
    },
    {
      cle: 'pispi', titre: 'PI-SPI', chemin: 'pispi',
      onglets: [
        ecran('configuration', 'Configuration', <PispiConfigurationPage />),
        ecran('compte', 'Compte, alias & QR', <PispiComptePage />),
      ],
    },
    ecran('enseignants', 'Vérification enseignants', <ReglagesEnseignantsPage />),
    ecran('moderation', 'Modération', <ReglagesModerationPage />),
    ecran('parrainage', 'Parrainage', <ReglagesParrainagePage />),
    ecran('videos', 'Vidéos', <ReglagesVideosCard />),
    ecran('alertes', 'Alertes', <ReglagesAlertesCard />),
  ],
};

const TECHNIQUE: Section = {
  cle: 'technique', titre: 'Technique', icone: <TechniqueIcon />, chemin: '/technique', roles: SUPER_ADMINS,
  onglets: [
    ecran('journaux', 'Journaux', <LogsPage />),
    {
      ...ecran('audit', 'Audit', <ActivityMonitorPage />),
      details: [{ chemin: ':logId', element: <ActivityLogDetailPage /> }],
    },
    ecran('crashs', 'Crashs', <CrashReportsPage />),
    {
      // Revue du 21/10 : un seul outil pour déplacer des données.
      cle: 'donnees', titre: 'Données', chemin: 'donnees',
      onglets: [
        ecran('copier', 'Copier entre environnements', <EnvSyncPage />),
        ecran('historique', 'Historique et restauration', <EnvSyncHistoryPage />),
        ecran('importer', 'Importer du contenu', <DataMigrationPage />),
      ],
    },
    ecran('simulateur-pispi', 'Simulateur PI-SPI', <PispiSimulateurPage />),
    ecran('webhooks', 'Webhooks', <PispiNotificationsPage />),
  ],
};

/** Les 8 sections, dans l'ordre du menu. */
export const SECTIONS: Section[] = [
  TABLEAU_DE_BORD, CONTENU, UTILISATEURS, MODERATION, PAIEMENTS, CROISSANCE, REGLAGES, TECHNIQUE,
];

/** Adresse d'accueil de la console. */
export const ADRESSE_ACCUEIL = TABLEAU_DE_BORD.chemin;
